import { createReducer, on } from '@ngrx/store';

import { filter, map } from 'lodash-es';
import states from 'states-us';

import { FormValidationErrors } from '../../typings/form-validation-errors';
import { SearchableSelectOption } from '../../typings/searchable-select';
import * as fromPersistedStateActions from '../actions/persisted-state.actions';
import * as fromSettingActions from '../actions/setting.actions';

export const settingFeatureKey = 'setting';

export interface State {
  alternateAppIconUsed: boolean;
  applicationKey: string | null;
  availableCounties: SearchableSelectOption[];
  availableStates: SearchableSelectOption[];
  enableTooltips: boolean;
  hasValidReferralCode: boolean;
  isDebugModeEnabled: boolean;
  parseType: string | null;
  referralCode: string | null;
  referralCodeFormValidationErrors: FormValidationErrors;
  selectedCounty: string | null;
  selectedState: string | null;
}

export const initialState: State = {
  alternateAppIconUsed: false,
  applicationKey: null,
  availableCounties: [],
  availableStates: getAvailableState(),
  enableTooltips: true,
  hasValidReferralCode: false,
  isDebugModeEnabled: false,
  parseType: null,
  referralCode: null,
  referralCodeFormValidationErrors: {},
  selectedCounty: null,
  selectedState: null
};

export function getAvailableState() {
  return map(
    filter(states, (state) => !state.territory),
    (item) => ({
      name: item.name,
      value: item.abbreviation
    })
  );
}

export const reducer = createReducer(
  initialState,
  on(fromSettingActions.loadSettings, (state, { payload }) => ({
    ...initialState,
    referralCode: payload.referralCode
  })),
  on(
    fromSettingActions.updateReferralCodeSuccess,
    fromPersistedStateActions.migratePersistedOldStateReferralCodeSuccess,
    (state, { payload }) => ({
      ...state,
      referralCode: payload.code
    })
  ),
  on(
    fromSettingActions.loadSettingsSuccess,
    fromSettingActions.updateReferralCodeSuccess,
    fromSettingActions.refreshReferralCodeSuccess,
    fromPersistedStateActions.migratePersistedOldStateReferralCodeSuccess,
    (state, { payload }) => ({
      ...state,
      applicationKey: payload.data.parse.appKey,
      hasValidReferralCode: payload.data.validCode,
      parseType: payload.data.parse.type,
      referralCodeFormValidationErrors: {}
    })
  ),
  on(fromSettingActions.updateApplicationIconSuccess, (state, { payload }) => ({
    ...state,
    alternateAppIconUsed: payload.alternateAppIconUsed
  })),
  on(fromSettingActions.updateSelectedState, (state, { payload }) => ({
    ...state,
    ...(state.selectedState !== payload.state
      ? {
          selectedCounty: null,
          selectedState: payload.state
        }
      : {
          selectedState: payload.state
        })
  })),
  on(fromSettingActions.updateAvailableCountiesSuccess, (state, { payload }) => ({
    ...state,
    availableCounties: map(payload, (item) => {
      return {
        name: item.countyName,
        value: item.countyName
      };
    })
  })),
  on(fromSettingActions.updateAvailableCountiesFailure, (state) => ({
    ...state,
    availableCounties: []
  })),
  on(fromSettingActions.updateSelectedCounty, (state, { payload }) => ({
    ...state,
    selectedCounty: payload.county
  })),
  on(fromSettingActions.toggleDebugMode, (state) => ({
    ...state,
    isDebugModeEnabled: !state.isDebugModeEnabled
  })),
  on(fromSettingActions.toggleEnableTooltips, (state) => ({
    ...state,
    enableTooltips: !state.enableTooltips
  })),
  on(fromSettingActions.loadSettingsFailure, fromSettingActions.updateReferralCodeFailure, (state, { payload }) => ({
    ...state,
    hasValidReferralCode: false,
    referralCodeFormValidationErrors: {
      code: {
        invalid: payload.error.message
      }
    }
  })),
  on(
    fromSettingActions.loadSettingsReset,
    fromPersistedStateActions.persistedStateReset,
    fromPersistedStateActions.loadPersistedStateFailure,
    () => ({
      ...initialState
    })
  ),
  on(fromPersistedStateActions.loadPersistedStateSuccess, (state, { payload }) => ({
    ...state,
    ...payload[settingFeatureKey],
    availableStates: getAvailableState()
  }))
);
